import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { createOrder } from '../../api/orders';
import { formatPrice, hasPrice } from '../../utils/price';
import { trackPurchase } from '../../utils/metrika';
import axios from 'axios';

const CheckoutPage = () => {
  const { user, loading } = useAuth();
  const { items, totalPrice, hasUnpricedItems, clearCart } = useCart();
  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    email: user?.email || '',
    company: user?.company || '',
    comment: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [createdOrder, setCreatedOrder] = useState<{ orderNumber: string } | null>(null);

  useEffect(() => {
    if (!user) return;
    setForm((current) => ({
      ...current,
      name: current.name || user.name || '',
      phone: current.phone || user.phone || '',
      email: current.email || user.email || '',
      company: current.company || user.company || ''
    }));
  }, [user]);

  if (loading) {
    return <div className="max-w-3xl mx-auto px-4 py-12">Загрузка...</div>;
  }

  if (!user) {
    return <Navigate to="/login?redirect=/checkout" replace />;
  }

  if (items.length === 0 && !createdOrder) {
    return <Navigate to="/cart" replace />;
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const order = await createOrder({
        items: items.map((item) => ({ subcategoryId: item.id, quantity: item.quantity })),
        comment: form.comment,
        customer: {
          name: form.name,
          phone: form.phone,
          email: form.email,
          company: form.company
        }
      });
      trackPurchase({
        orderNumber: order.orderNumber,
        total: order.total,
        items: order.items.map((item) => ({
          id: item.subcategoryId,
          name: item.name,
          price: item.price ?? 0,
          quantity: item.quantity
        }))
      });
      clearCart();
      setCreatedOrder(order);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.message || 'Не удалось оформить заказ');
      } else {
        setError('Не удалось оформить заказ');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (createdOrder) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-md p-8 text-center">
          <h1 className="text-3xl font-bold mb-4">Заказ {createdOrder.orderNumber}</h1>
          <p className="text-xl font-semibold text-primary mb-3">Обрабатывается</p>
          <p className="text-slate-600 mb-6">
            Мы получили ваш заказ и перезвоним в ближайшее время.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/orders" className="btn-primary">Мои заказы</Link>
            <Link to="/catalog" className="btn-secondary">Вернуться в каталог</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Оформление заказа</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <form onSubmit={handleSubmit} className="lg:col-span-2 bg-white rounded-2xl shadow-md p-6 space-y-4">
          {error && <div className="bg-red-50 text-red-600 p-3 rounded-md">{error}</div>}
          <div>
            <label className="block text-sm font-medium mb-1">Имя *</label>
            <input name="name" required value={form.name} onChange={handleChange} className="w-full px-4 py-2 border rounded-md" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Телефон *</label>
            <input name="phone" required value={form.phone} onChange={handleChange} className="w-full px-4 py-2 border rounded-md" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email *</label>
            <input name="email" type="email" required value={form.email} onChange={handleChange} className="w-full px-4 py-2 border rounded-md" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Компания</label>
            <input name="company" value={form.company} onChange={handleChange} className="w-full px-4 py-2 border rounded-md" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Комментарий к заказу</label>
            <textarea name="comment" rows={4} value={form.comment} onChange={handleChange} className="w-full px-4 py-2 border rounded-md" />
          </div>
          <button type="submit" disabled={submitting} className="btn-primary w-full disabled:opacity-50">
            {submitting ? 'Отправка...' : 'Подтвердить заказ'}
          </button>
        </form>
        <div className="bg-white rounded-2xl shadow-md p-6 h-fit">
          <h2 className="text-xl font-bold mb-4">Ваш заказ</h2>
          <div className="space-y-2 mb-4">
            {items.map((item) => (
              <div key={item.id} className="flex justify-between text-sm gap-3">
                <span>{item.name} × {item.quantity}</span>
                <span className="font-semibold">
                  {hasPrice(item.price) ? formatPrice(item.price * item.quantity) : 'Цена по запросу'}
                </span>
              </div>
            ))}
          </div>
          <p className="text-2xl font-extrabold text-primary">{formatPrice(totalPrice)}</p>
          {hasUnpricedItems && (
            <p className="text-sm text-amber-800 mt-2">Часть позиций без цены — менеджер уточнит стоимость.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
